import { siteApi } from "@api";
import { PRIZE_PAGES } from "@constants";
import useAuthSession from "@hooks/useAuthSession";
import useUserMode from "@hooks/useUserMode";
import { getPromotionPageLandedCount } from "@utils/promotion-tracking";
import { AccountVariableContent, AccountVariableKey, AccountVariableModel } from "kz-ui-sdk";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

const usePromotionBottomPromptVisibility = (isPromotionsClaimable: boolean) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { isGuest } = useUserMode();
  const { self } = useAuthSession();
  const memberId = self?.member?.id;

  const { data: promotionSettingsData } = siteApi.useGetAccountVariableQuery(
    {
      query: {
        key: AccountVariableKey.PromotionSettings,
      },
    },
    {
      skip: isGuest,
    },
  );

  const promotionSettings = useMemo(() => {
    if (!promotionSettingsData?.entries) return undefined;
    const entry = promotionSettingsData.entries.find((entry) => entry.key === AccountVariableKey.PromotionSettings) as
      | AccountVariableModel<AccountVariableContent[AccountVariableKey.PromotionSettings]>
      | undefined;
    return entry?.content;
  }, [promotionSettingsData?.entries]);

  const shouldShowPrompt = useMemo(() => {
    if (isGuest) {
      return false;
    }

    const isOnHiddenPage = PRIZE_PAGES.has(currentPath);
    if (isOnHiddenPage) {
      return false;
    }

    if (!isPromotionsClaimable) {
      return false;
    }

    if (!promotionSettings?.bottomPrompt.enabled) {
      return false;
    }

    if (!memberId) {
      return false;
    }

    const landedCount = getPromotionPageLandedCount(memberId);
    const maxCount = promotionSettings.bottomPrompt.maxPromotionLandedCount;
    return landedCount <= maxCount;
  }, [isGuest, currentPath, isPromotionsClaimable, promotionSettings, memberId]);

  return {
    shouldShowPrompt,
  };
};

export default usePromotionBottomPromptVisibility;
